import { formatCurrency } from "../../popup/finances/utils/helpers";
import Tooltip from "../../Tooltip";
import { getBudgetColor } from "../utils/dynamicColorsBar";

const parseBudgetNumber = (value, fallback = 0) => {
  if (typeof value === "number")
    return Number.isFinite(value) ? value : fallback;
  if (typeof value !== "string") return fallback;

  const cleanedValue = value.trim().replace(/[^\d.,-]/g, "");
  if (!cleanedValue) return fallback;

  const hasComma = cleanedValue.includes(",");
  const hasDot = cleanedValue.includes(".");
  const commaParts = cleanedValue.split(",");
  const normalizedValue =
    hasComma && !hasDot && commaParts.length === 2 && commaParts[1].length < 3
      ? cleanedValue.replace(",", ".")
      : cleanedValue.replace(/,/g, "");
  const parsedValue = Number.parseFloat(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const normalizeThreshold = (value) => {
  const parsedThreshold = parseBudgetNumber(value, 0.85);

  return parsedThreshold > 1 ? parsedThreshold / 100 : parsedThreshold;
};

function ProgressBar({
  spent = 0,
  total = 1,
  threshold = 0.85,
  getColor = getBudgetColor,
  showLabels = false,
  percentage = null,
  variant = "default",
  tooltipConfig = {},
  renderLabel = null,
  children = null,
}) {
  const calculatedTotal = total > 0 ? total : 1;
  const rawUsage = (spent / calculatedTotal) * 100;

  const calculatedUsage = Number.isFinite(rawUsage)
    ? Math.min(Math.max(rawUsage, 0), 100)
    : 0;

  const usagePercentageStr = `${calculatedUsage.toFixed(0)}%`;
  const barColor = getColor(spent, total, threshold);

  const isPercentageProvided = percentage !== null && percentage !== undefined;
  const displayPercentage = isPercentageProvided
    ? percentage
    : calculatedUsage.toFixed(0);

  const defaultLabel =
    variant === "fraction" ? (
      <div
        className="progress-bar-label"
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "20px",
          justifyContent: "space-between",
        }}
      >
        <div>
          <Tooltip
            content={tooltipConfig?.spent?.content}
            title={tooltipConfig?.spent?.title}
          >
            <span className="progress-bar-spent">{formatCurrency(spent)}</span>
          </Tooltip>

          <span className="progress-bar-versus"> from </span>

          <Tooltip
            content={tooltipConfig?.total?.content}
            title={tooltipConfig?.total?.title}
          >
            <span className="progress-bar-total">{formatCurrency(total)}</span>
          </Tooltip>
        </div>
        <div>
          <Tooltip
            content={tooltipConfig?.percentUsage?.content}
            title={tooltipConfig?.percentUsage?.title}
          >
            <span className="progress-bar-total">
              {tooltipConfig?.percentUsage?.percent} %
            </span>
          </Tooltip>
        </div>
      </div>
    ) : null;

  return (
    <div className="progress-bar-wrapper">
      {/* LABEL AREA */}
      {showLabels && (
        <div className="progress-bar-label">
          {renderLabel
            ? renderLabel({
                spent: spent,
                total: total,
                percentage: displayPercentage,
              })
            : defaultLabel}
        </div>
      )}

      {/* BAR CONTAINER WITH CENTERED CONTENT */}
      <div
        className="progress-bar-container"
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden", // Ensures fill & text stay cleanly within borders
        }}
      >
        <div
          className="progress-bar-fill"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: `${calculatedUsage}%`,
            minWidth: spent > 0 && calculatedUsage > 0 ? "8px" : 0,
            background: barColor,
            backgroundColor: barColor,
            transition: "width 0.3s ease",
          }}
          title={usagePercentageStr}
          role="progressbar"
          aria-valuenow={calculatedUsage}
          aria-valuemin={0}
          aria-valuemax={100}
        />

        {/* ALWAYS CENTERED PERCENTAGE TEXT */}
        {isPercentageProvided && (
          <span
            className="progress-bar-percentage-label"
            style={{
              position: "relative",
              zIndex: 2, // Keeps text readable above fill
              fontSize: "12px",
              fontWeight: "600",
              color: calculatedUsage > 50 ? "#FFFFFF" : "#001D38",
              pointerEvents: "none",
            }}
          >
            {displayPercentage}%
          </span>
        )}

        {/* OPTIONAL SLOT OVERLAY */}
        {children}
      </div>
    </div>
  );
}

export default ProgressBar;
